import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../../../components/Common/Loader';
import { Toast } from '../../../../components/Common/Toast';
import { BULK_PURCHASE_BATCH_SIZE, bulkMarkBooksPurchased } from '../services/Classrooms.service';

// Shared selection + bulk "Mark books as purchased" logic, used by both the Classroom and Project
// book views so they stay consistent. The parent supplies how to gather "select all" ids (whole
// classroom vs a single project) and what to refetch when done.
export interface BulkMarkPurchased {
  selectMode: boolean;
  enterSelectMode: () => void;
  exitSelectMode: () => void;
  selectedIds: Set<string>;
  toggleSelect: (bookId: string) => void;
  clear: () => void;
  reference: string;
  setReference: (v: string) => void;
  bulkRunning: boolean;
  bulkProgress: { done: number; total: number } | null;
  selectAll: () => Promise<void>;
  selectAllLoading: boolean;
  run: () => Promise<void>;
}

export function useBulkMarkPurchased(opts: {
  classroomId: string;
  teacherEmail?: string;
  onCompleted: () => void | Promise<void>;
  // Returns the ids to select for "select all" (already filtered to not-yet-purchased books).
  getSelectAllIds: () => Promise<string[]>;
}): BulkMarkPurchased {
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reference, setReference] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  const toggleSelect = (bookId: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(bookId)) {
        next.delete(bookId);
      } else {
        next.add(bookId);
      }
      return next;
    });

  const clear = () => setSelectedIds(new Set());
  const enterSelectMode = () => setSelectMode(true);
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setReference('');
  };

  const selectAll = async () => {
    setSelectAllLoading(true);
    try {
      const ids = await opts.getSelectAllIds();
      setSelectedIds(new Set(ids));
      if (ids.length === 0) Toast('No unpurchased books to select', false);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const run = async () => {
    const ids = [...selectedIds];
    if (!ids.length || !opts.classroomId) return;

    const confirmed = window.confirm(
      `Mark ${ids.length} book(s) as purchased (prepaid, $0)${opts.teacherEmail ? ` for ${opts.teacherEmail}` : ''}?\n\n` +
        `The teacher will then be able to ship them. Re-marking a book that already has an unshipped ` +
        `purchase adds another copy to that order.`,
    );
    if (!confirmed) return;

    setBulkRunning(true);
    setBulkProgress({ done: 0, total: ids.length });
    let ok = 0;
    let skipped = 0;
    let failed = 0;
    let sampleError = '';
    const ref = reference.trim() || undefined;

    try {
      for (let i = 0; i < ids.length; i += BULK_PURCHASE_BATCH_SIZE) {
        const chunk = ids.slice(i, i + BULK_PURCHASE_BATCH_SIZE);
        const results = await bulkMarkBooksPurchased(opts.classroomId, chunk, 1, ref);
        for (const r of results) {
          if (!r.success) {
            failed += 1;
            if (!sampleError && r.error) sampleError = r.error;
          } else if (r.skipped) {
            skipped += 1;
          } else {
            ok += 1;
          }
        }
        setBulkProgress({ done: ok + skipped + failed, total: ids.length });
      }
    } finally {
      setBulkRunning(false);
      setBulkProgress(null);
    }

    if (failed === 0) {
      Toast(`Marked ${ok} book(s) as purchased${skipped ? `, ${skipped} skipped` : ''}`, false);
    } else if (ok === 0 && skipped === 0) {
      Toast({ title: 'Could not mark as purchased', message: sampleError || `All ${failed} failed.` }, true);
    } else {
      Toast(
        { title: 'Completed with issues', message: `${ok} purchased, ${skipped} skipped, ${failed} failed.${sampleError ? ` ${sampleError}` : ''}` },
        true,
      );
    }

    exitSelectMode();
    await opts.onCompleted();
  };

  return {
    selectMode,
    enterSelectMode,
    exitSelectMode,
    selectedIds,
    toggleSelect,
    clear,
    reference,
    setReference,
    bulkRunning,
    bulkProgress,
    selectAll,
    selectAllLoading,
    run,
  };
}

export const BulkPurchaseToolbar: React.FC<{ bulk: BulkMarkPurchased; selectAllLabel?: string }> = ({
  bulk,
  selectAllLabel = 'Select all',
}) => (
  <div className="flex flex-wrap items-center gap-3 mb-3">
    {!bulk.selectMode ? (
      <button
        type="button"
        onClick={bulk.enterSelectMode}
        className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-storyblue rounded-md px-3 py-2 hover:opacity-90"
        title="Select books to mark as purchased for a prepaid school"
      >
        <FontAwesomeIcon icon={faCartShopping} />
        Mark books as purchased
      </button>
    ) : (
      <div className="flex flex-wrap items-center gap-3 w-full bg-storyblue/5 border border-storyblue/20 rounded-md p-3">
        <span className="text-sm font-semibold text-storyblue">{bulk.selectedIds.size} selected</span>
        <button
          type="button"
          onClick={bulk.selectAll}
          disabled={bulk.selectAllLoading || bulk.bulkRunning}
          className="inline-flex items-center gap-2 text-sm font-semibold text-storyblue border border-storyblue rounded-md px-3 py-1.5 hover:bg-storyblue/10 disabled:opacity-50"
        >
          {bulk.selectAllLoading && <Loader loader={true} dimentions={'w-4 h-4'} />}
          {selectAllLabel}
        </button>
        <button
          type="button"
          onClick={bulk.clear}
          disabled={bulk.bulkRunning || bulk.selectedIds.size === 0}
          className="text-sm font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-50"
        >
          Clear
        </button>
        <input
          type="text"
          value={bulk.reference}
          onChange={(e) => bulk.setReference(e.target.value)}
          placeholder="Reference / PO (optional)"
          disabled={bulk.bulkRunning}
          className="border border-gray-300 rounded px-2 py-1.5 text-sm flex-1 min-w-[12rem]"
        />
        <button
          type="button"
          onClick={bulk.run}
          disabled={bulk.bulkRunning || bulk.selectedIds.size === 0}
          className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-storygreen rounded-md px-3 py-2 hover:opacity-90 disabled:opacity-50"
        >
          {bulk.bulkRunning && <Loader loader={true} dimentions={'w-4 h-4'} />}
          {bulk.bulkRunning && bulk.bulkProgress
            ? `Marking ${bulk.bulkProgress.done}/${bulk.bulkProgress.total}…`
            : `Mark ${bulk.selectedIds.size} as purchased`}
        </button>
        <button
          type="button"
          onClick={bulk.exitSelectMode}
          disabled={bulk.bulkRunning}
          className="text-sm font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    )}
  </div>
);
